import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, TrendingUp, BookOpen, Sparkles, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { loginWithEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/trend-spotter';
  const search = location.state?.from?.search || '';

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`Verification code sent to ${email}. Please check your email.`);
        setStep('code');
      } else {
        setMessage(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      setMessage('An error occurred. Please try again.');
      console.error('Error sending code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const success = await loginWithEmail(email, code);
    if (success) {
      navigate(from + search, { replace: true });
    } else {
      setMessage('Invalid verification code. Please try again.');
    }
    setLoading(false);
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setMessage('');
  };

  const features = [
    {
      icon: TrendingUp,
      title: 'Emerging trends',
      text: 'See what’s climbing on TikTok & Instagram before it peaks. Filter by platform, category, and momentum.',
    },
    {
      icon: BookOpen,
      title: 'Swipe file',
      text: 'Sync bookmarks from TikTok & Instagram with our Chrome extension—tag, sort, and share inspiration.',
    },
    {
      icon: Sparkles,
      title: 'Trend → brief',
      text: 'Turn any trend into an on-brand brief (angle, hook, format). Manage scripts and shot lists on your board.',
    },
    {
      icon: Calendar,
      title: 'Team workflow',
      text: 'Mobile-friendly Content Board to move briefs through production and collaborate with your team.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-purple-50/80 to-pink-50 py-10 sm:py-14 px-4 sm:px-6">
      <div className="w-full max-w-xl mx-auto flex flex-col items-center text-center">
        {/* Brand */}
        <div className="flex flex-col items-center gap-2 mb-5">
          <img src="/showstop-logo.png" alt="ShowStop" className="h-14 w-auto object-contain" />
          <p className="text-xs font-medium text-indigo-600 tracking-wide uppercase">Trend Spotter</p>
        </div>

        {/* Hook */}
        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight mb-6 max-w-md mx-auto">
          Catch trends early. Brief in minutes.
        </h1>

        {/* Feature grid */}
        <div className="w-full grid sm:grid-cols-2 gap-3 mb-6 text-left">
          {features.map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="rounded-xl bg-white/90 border border-indigo-100/80 shadow-sm px-3 py-3 flex gap-3 items-start"
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                <Icon className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-xs sm:text-sm">{title}</p>
                <p className="text-gray-600 text-xs mt-0.5 leading-snug">{text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Sign-in card */}
        <div className="w-full bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-gray-200/90 p-6 sm:p-8 space-y-6 text-left">
          <div className="text-center">
            <h2 className="text-2xl font-extrabold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Sign in
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {step === 'email'
                ? 'Passwordless: we’ll email you a one-time 6-digit code.'
                : 'Enter the code we sent to your inbox.'}
            </p>
          </div>

          {step === 'email' ? (
            <form className="space-y-5" onSubmit={handleEmailSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Work email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none relative block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              {message && (
                <div
                  className={`p-3 rounded-lg text-sm ${
                    message.includes('sent')
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 rounded-lg text-white font-medium bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 shadow-md"
              >
                {loading ? 'Sending…' : 'Send verification code'}
              </button>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={handleCodeSubmit}>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification code
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="code"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="6"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="appearance-none relative block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    autoFocus
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500 text-center">
                  Sent to <span className="font-medium text-gray-800">{email}</span>
                </p>
              </div>

              {message && (
                <div className="p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200">{message}</div>
              )}

              <div className="space-y-2">
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full py-3 px-4 rounded-lg text-white font-medium bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 shadow-md"
                >
                  {loading ? 'Verifying…' : 'Verify & continue'}
                </button>
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="w-full text-sm text-gray-600 hover:text-gray-900 py-1"
                >
                  ← Use a different email
                </button>
              </div>

              <p className="text-center text-xs text-gray-500">
                Didn’t get it?{' '}
                <button type="button" onClick={handleEmailSubmit} className="text-indigo-600 font-medium hover:underline">
                  Resend code
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
